import { useEffect, useRef, useState } from "react";
import { supabase } from "../lib/supabase";
import { EquipmentIcon, PieceIcon } from "./EquipmentIcon";
import {
  GLYPH_PREFIX, ICON_BUCKET, ICON_UPLOAD_OPEN, NAME_MAX, customKey, equipmentName, iconObjectPath,
  isCustomKey, originalName, randomId6, type EquipmentSetting,
} from "../lib/equipmentLibrary";
import { EQUIPMENT_GROUPS } from "../lib/equipment";
import { EQUIPMENT_GLYPH_OF } from "../lib/equipmentGlyphs";
import { rasteriseIcon } from "../lib/equipmentIconRaster";

/* THE EQUIPMENT PAGE'S TWO WINDOWS (W62 step C(d)) — the edit window (a name and an icon) and the
 * question asked before anything a click cannot take back (restore all, delete his own piece). Kept
 * beside the other windows rather than in the route, as `AssignProgramWindow` and `DiscardWindow`
 * are: each is its own surface with its own one accent button. */

export type EditTarget = { mode: "new" | "edit"; key: string | null; name: string; iconPath: string | null };
export type EquipmentConfirm =
  | { kind: "restoreAll"; keys: readonly string[] }
  | { kind: "delete"; key: string; label: string; usedBy: number };

/* ── the edit window: a name and an icon, nothing else ── */

export function EquipmentEditor({ target, settings, onClose, onSave }: {
  target: EditTarget | null;
  settings: readonly EquipmentSetting[];
  onClose: () => void;
  /** Resolves to a refusal sentence, or null when saved. */
  onSave: (key: string, name: string, iconPath: string | null) => Promise<string | null>;
}) {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const nameRef = useRef<HTMLInputElement>(null);
  const [name, setName] = useState("");
  const [iconPath, setIconPath] = useState<string | null>(null);
  const [picking, setPicking] = useState(false);
  const [busy, setBusy] = useState(false);
  const [refusal, setRefusal] = useState<string | null>(null);
  const open = target !== null;

  useEffect(() => {
    const el = dialogRef.current;
    if (el === null) return;
    if (open && !el.open) el.showModal();
    if (!open && el.open) el.close();
    if (open && target !== null) {
      setName(target.name); setIconPath(target.iconPath); setPicking(false); setRefusal(null); setBusy(false);
    }
  }, [open, target]);
  useEffect(() => { if (open) nameRef.current?.focus(); }, [open]);

  const builtInKey = target?.key !== null && target?.key !== undefined && !isCustomKey(target.key) ? target.key : null;
  const placeholder = builtInKey !== null ? originalName(builtInKey) : "e.g. Wooden rings";
  const previewKey = target?.key ?? "";

  async function upload(file: File) {
    setRefusal(null); setBusy(true);
    try {
      const { png, isMask } = await rasteriseIcon(file);
      const { data } = await supabase.auth.getSession();
      const id = data.session?.user.id;
      if (id === undefined) throw new Error("You are signed out. Sign in again.");
      const path = iconObjectPath(id, target?.key ?? "new", isMask, Date.now());
      const { error } = await supabase.storage.from(ICON_BUCKET).upload(path, png, { contentType: "image/png", upsert: false });
      if (error !== null) throw new Error(error.message);
      setIconPath(path);
    } catch (e) {
      setRefusal(e instanceof Error ? e.message : "The icon could not be uploaded.");
    } finally {
      setBusy(false);
    }
  }

  async function save() {
    if (target === null) return;
    setBusy(true); setRefusal(null);
    const key = target.key ?? customKey(name, randomId6());
    const failure = await onSave(key, name, iconPath);
    setBusy(false);
    if (failure !== null) { setRefusal(failure); return; }
    onClose();
  }

  const title = target?.mode === "new" ? "New equipment" : `Edit ${target?.key !== null && target?.key !== undefined ? equipmentName(target.key, settings) : ""}`;

  return (
    <dialog ref={dialogRef} className="ht-window ht-confirm equipment-editor" aria-labelledby="equipment-editor-title"
            onCancel={(e) => { e.preventDefault(); onClose(); }}>
      <div className="ht-window-frame ht-confirm-frame">
        <h2 className="ht-title ht-confirm-title" id="equipment-editor-title">{title}</h2>

        <label className="equipment-editor-field">
          <span className="equipment-editor-label">Name</span>
          <input ref={nameRef} className="ht-input" value={name} maxLength={NAME_MAX} placeholder={placeholder}
                 onChange={(e) => setName(e.target.value)}
                 onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); void save(); } }} />
        </label>
        {builtInKey !== null && (
          <p className="equipment-editor-hint">Leave it empty to keep the app's name, {originalName(builtInKey)}.</p>
        )}

        <div className="equipment-editor-field">
          <span className="equipment-editor-label">Icon</span>
          <div className="equipment-editor-icon-row">
            <span className="equipment-row-icon"><PieceIcon equipmentKey={builtInKey ?? previewKey} iconPath={iconPath} size={28} /></span>
            <button type="button" className="ht-button ht-button-quiet" aria-expanded={picking}
                    onClick={() => setPicking(!picking)}>Choose one of ours</button>
            {ICON_UPLOAD_OPEN && (
              <label className="ht-button ht-button-quiet equipment-editor-upload">
                Upload
                <input type="file" accept="image/png,image/jpeg,image/webp,image/svg+xml" hidden
                       onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; if (f !== undefined) void upload(f); }} />
              </label>
            )}
            {iconPath !== null && (
              <button type="button" className="ht-button ht-button-quiet" onClick={() => setIconPath(null)}>
                {builtInKey !== null ? "Use the app's icon" : "No icon"}
              </button>
            )}
          </div>
          {ICON_UPLOAD_OPEN && <p className="equipment-editor-hint">Square PNG with a transparent background works best.</p>}
          {picking && (
            <div className="equipment-glyph-grid" role="group" aria-label="Our icons">
              {EQUIPMENT_GROUPS.flatMap((g) => g.keys).filter((k) => EQUIPMENT_GLYPH_OF[k] !== undefined).map((k) => {
                const chosen = iconPath === `${GLYPH_PREFIX}${k}` || (iconPath === null && k === builtInKey);
                return (
                  <button key={k} type="button" className="equipment-glyph-choice" aria-pressed={chosen}
                          aria-label={originalName(k)} title={originalName(k)}
                          onClick={() => { setIconPath(`${GLYPH_PREFIX}${k}`); setPicking(false); }}>
                    <EquipmentIcon equipmentKey={k} size={22} />
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {refusal !== null && <p className="ht-callout workouts-error" role="alert">{refusal}</p>}

        <div className="ht-confirm-actions">
          <button type="button" className="ht-button" onClick={onClose}>Cancel</button>
          <button type="button" className="ht-button ht-button-primary" disabled={busy} onClick={() => { void save(); }}>
            {busy ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </dialog>
  );
}

/* ── the two questions that cannot be undone by a click ── */

export function EquipmentConfirmWindow({ confirm, onCancel, onConfirm }: {
  confirm: EquipmentConfirm | null; onCancel: () => void; onConfirm: () => void;
}) {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const cancelRef = useRef<HTMLButtonElement>(null);
  const open = confirm !== null;
  useEffect(() => {
    const el = dialogRef.current;
    if (el === null) return;
    if (open && !el.open) el.showModal();
    if (!open && el.open) el.close();
  }, [open]);
  useEffect(() => { if (open) cancelRef.current?.focus(); }, [open]);

  const n = confirm?.kind === "restoreAll" ? confirm.keys.length : 0;
  return (
    <dialog ref={dialogRef} className="ht-window ht-confirm" aria-labelledby="equipment-confirm-title"
            onCancel={(e) => { e.preventDefault(); onCancel(); }}>
      <div className="ht-window-frame ht-confirm-frame">
        <h2 className="ht-title ht-confirm-title" id="equipment-confirm-title">
          {confirm?.kind === "delete" ? `Delete ${confirm.label}?` : "Restore all defaults?"}
        </h2>
        <p className="ht-confirm-sentence">
          {confirm?.kind === "delete"
            ? (confirm.usedBy > 0
                ? `${String(confirm.usedBy)} of your exercises use it. They keep it, and it will show by the name it was created with.`
                : "It is not used by any of your exercises.")
            : `${String(n)} piece${n === 1 ? "" : "s"} you changed go back to how the app ships them — names, icons and hidden. Your own equipment is not touched.`}
        </p>
        <div className="ht-confirm-actions">
          <button type="button" className="ht-button" ref={cancelRef} onClick={onCancel}>Cancel</button>
          <button type="button" className={confirm?.kind === "delete" ? "ht-button ht-button-danger ht-confirm-go" : "ht-button ht-button-primary"}
                  onClick={onConfirm}>
            {confirm?.kind === "delete" ? "Delete" : "Restore"}
          </button>
        </div>
      </div>
    </dialog>
  );
}
